import { useState, useEffect, useMemo } from 'react';
import type { HolidayItem, HolidayType, HolidayAppliesTo } from './holidayTypes';
import { computeDuration, formatDateRange, typeLabels, appliesToLabels } from './holidayTypes';
import {
    fetchHolidays, createHoliday, deleteHoliday,
    type HolidayApiRecord,
} from '../../services/holidayApi';
import { useTranslation } from 'react-i18next';
import { getSchoolId, getAcademicSessionId } from '../../services/auth';
import './HolidayLedger.css';

/* ═══════════════════════════════════════
   Helper: map API record → HolidayItem
   ═══════════════════════════════════════ */
function mapApiToItem(r: HolidayApiRecord): HolidayItem {
    const startDate = r.start_date.includes('T') ? r.start_date.split('T')[0] : r.start_date;
    const endDate = r.end_date.includes('T') ? r.end_date.split('T')[0] : r.end_date;
    return {
        id: r.id,
        name: r.name,
        type: r.type,
        applies_to: r.applies_to,
        start_date: startDate,
        end_date: endDate,
        description: r.description,
        duration: computeDuration(startDate, endDate),
    };
}

/* ═══════════════════════════════════════
   Calendar helpers
   ═══════════════════════════════════════ */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
    return new Date(year, month, 1).getDay();
}

function toYYYYMMDD(year: number, month: number, day: number): string {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/* ═══════════════════════════════════════
   Component
   ═══════════════════════════════════════ */
type ViewMode = 'list' | 'calendar';

export default function HolidayLedger() {
    const { t } = useTranslation();

    /* ── Data state ── */
    const [holidays, setHolidays] = useState<HolidayItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('list');

    /* ── Calendar state ── */
    const today = new Date();
    const [calYear, setCalYear] = useState(today.getFullYear());
    const [calMonth, setCalMonth] = useState(today.getMonth());

    /* ── Drawer state ── */
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ── Form state ── */
    const [formName, setFormName] = useState('');
    const [formType, setFormType] = useState<HolidayType>('public');
    const [formAppliesTo, setFormAppliesTo] = useState<HolidayAppliesTo>('all');
    const [formStartDate, setFormStartDate] = useState('');
    const [formEndDate, setFormEndDate] = useState('');
    const [formDesc, setFormDesc] = useState('');

    /* ── Load holidays on mount ── */
    useEffect(() => {
        setLoading(true);
        const schoolId = getSchoolId() ?? undefined;
        const academicSessionId = getAcademicSessionId() ?? undefined;
        fetchHolidays({ perPage: 200, schoolId, academicSessionId })
            .then((res) => {
                const items = res.data.map(mapApiToItem);
                // Sort chronologically by start_date ascending
                items.sort((a, b) => a.start_date.localeCompare(b.start_date));
                setHolidays(items);
            })
            .catch(() => {
                showToast(t('holidays.failedToLoad'), 'error');
            })
            .finally(() => setLoading(false));
    }, []);

    /* ── Toast helper ── */
    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        if (type === 'error') setErrorMsg(msg); else setSuccessMsg(msg);
        setTimeout(() => { setSuccessMsg(''); setErrorMsg(''); }, 5000);
    };

    /* ── Open drawer ── */
    const openDrawer = () => {
        setFormName('');
        setFormType('public');
        setFormAppliesTo('all');
        setFormStartDate('');
        setFormEndDate('');
        setFormDesc('');
        setErrorMsg('');
        setDrawerOpen(true);
    };

    /* ── Auto-copy start_date → end_date ── */
    const handleStartDateChange = (value: string) => {
        setFormStartDate(value);
        // Speed feature: auto-fill end_date when start_date is set
        if (value && !formEndDate) {
            setFormEndDate(value);
        } else if (value && formEndDate && value > formEndDate) {
            setFormEndDate(value);
        }
    };

    /* ── Submit holiday ── */
    const handleSubmit = async () => {
        if (!formName.trim() || !formStartDate || !formEndDate) return;
        const schoolId = getSchoolId();
        if (!schoolId) {
            setErrorMsg(t('holidays.noSchool'));
            return;
        }
        const academicSessionId = getAcademicSessionId();
        if (!academicSessionId) {
            setErrorMsg(t('holidays.noSession'));
            return;
        }
        setSaving(true);
        setErrorMsg('');

        try {
            const apiRecord = await createHoliday({
                school_id: schoolId,
                academic_session_id: academicSessionId,
                name: formName,
                type: formType,
                applies_to: formAppliesTo,
                start_date: formStartDate,
                end_date: formEndDate,
                description: formDesc || null,
            });

            const newItem = mapApiToItem(apiRecord);
            setHolidays((prev) => {
                const updated = [newItem, ...prev];
                updated.sort((a, b) => a.start_date.localeCompare(b.start_date));
                return updated;
            });
            setDrawerOpen(false);
            showToast(t('holidays.addedSuccess'));
        } catch (err: unknown) {
            const apiErr = err as { errors?: Record<string, string[]>; message?: string };
            if (apiErr.errors) {
                setErrorMsg(Object.values(apiErr.errors).flat().join(' '));
            } else {
                setErrorMsg(apiErr.message || t('holidays.failedToCreate'));
            }
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete holiday ── */
    const handleDelete = async (id: string) => {
        try {
            await deleteHoliday(id);
            setHolidays((prev) => prev.filter((h) => h.id !== id));
            showToast(t('holidays.removedSuccess'));
        } catch {
            showToast(t('holidays.failedToDelete'), 'error');
        }
    };

    const formValid = formName.trim().length > 0 && formStartDate && formEndDate && formEndDate >= formStartDate;

    /* ── Calendar data: holidays for current month ── */
    const calHolidaysByDay = useMemo(() => {
        const map: Record<number, HolidayItem[]> = {};
        const monthStart = toYYYYMMDD(calYear, calMonth, 1);
        const monthEnd = toYYYYMMDD(calYear, calMonth, getDaysInMonth(calYear, calMonth));

        holidays.forEach((h) => {
            // Check if holiday overlaps with current month
            if (h.end_date < monthStart || h.start_date > monthEnd) return;
            const firstDay = Math.max(1, h.start_date > monthStart ? parseInt(h.start_date.split('-')[2]) : 1);
            const lastDay = Math.min(getDaysInMonth(calYear, calMonth), h.end_date < monthEnd ? parseInt(h.end_date.split('-')[2]) : getDaysInMonth(calYear, calMonth));
            for (let d = firstDay; d <= lastDay; d++) {
                if (!map[d]) map[d] = [];
                map[d].push(h);
            }
        });
        return map;
    }, [holidays, calYear, calMonth]);

    const todayStr = toYYYYMMDD(today.getFullYear(), today.getMonth(), today.getDate());

    /* ── Type options ── */
    const typeOptions: { value: HolidayType; label: string }[] = [
        { value: 'public', label: typeLabels.public },
        { value: 'academic_break', label: typeLabels.academic_break },
        { value: 'restricted', label: typeLabels.restricted },
        { value: 'emergency', label: typeLabels.emergency },
    ];

    const appliesToOptions: { value: HolidayAppliesTo; label: string }[] = [
        { value: 'all', label: appliesToLabels.all },
        { value: 'students_only', label: appliesToLabels.students_only },
        { value: 'staff_only', label: appliesToLabels.staff_only },
    ];

    return (
        <div className="holiday">
            {/* Toasts */}
            {successMsg && (
                <div className="holiday__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    {successMsg}
                    <button className="holiday__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && !drawerOpen && (
                <div className="holiday__toast holiday__toast--error">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="holiday__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="holiday__header">
                <div className="holiday__header-text">
                    <h1 className="holiday__title">Holidays & Calendar</h1>
                    <p className="holiday__subtitle">
                        Manage school holidays, academic breaks, and restricted days.
                    </p>
                </div>
                <div className="holiday__header-actions">
                    <div className="holiday__view-toggle">
                        <button
                            className={`holiday__view-btn ${viewMode === 'list' ? 'holiday__view-btn--active' : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                            List
                        </button>
                        <button
                            className={`holiday__view-btn ${viewMode === 'calendar' ? 'holiday__view-btn--active' : ''}`}
                            onClick={() => setViewMode('calendar')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                            Calendar
                        </button>
                    </div>
                    <button className="holiday__add-btn" onClick={openDrawer}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add Holiday
                    </button>
                </div>
            </div>

            {/* ═══ Content Area ═══ */}
            {loading ? (
                <div className="holiday__loading">
                    <div className="holiday__loader">
                        <span className="holiday__loader-dot" />
                        <span className="holiday__loader-dot" />
                        <span className="holiday__loader-dot" />
                    </div>
                </div>
            ) : viewMode === 'list' ? (
                /* ─── List View ─── */
                holidays.length === 0 ? (
                    <div className="holiday__empty-state">
                        <div className="holiday__empty-icon">
                            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                        </div>
                        <h3 className="holiday__empty-title">No Holidays Defined</h3>
                        <p className="holiday__empty-text">Click "Add Holiday" to add your first holiday or break.</p>
                    </div>
                ) : (
                    <div className="holiday__grid-wrap">
                        <table className="holiday__table">
                            <thead>
                                <tr>
                                    <th className="holiday__th">Holiday Name</th>
                                    <th className="holiday__th">Date Range</th>
                                    <th className="holiday__th">Duration</th>
                                    <th className="holiday__th">Type</th>
                                    <th className="holiday__th">Applies To</th>
                                    <th className="holiday__th holiday__th--actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holidays.map((h) => (
                                    <tr className="holiday__row" key={h.id}>
                                        <td className="holiday__td holiday__td--name">{h.name}</td>
                                        <td className="holiday__td holiday__td--date">
                                            {formatDateRange(h.start_date, h.end_date)}
                                        </td>
                                        <td className="holiday__td holiday__td--duration">
                                            {h.duration} {h.duration === 1 ? 'Day' : 'Days'}
                                        </td>
                                        <td className="holiday__td">
                                            <span className={`holiday__badge holiday__badge--${h.type}`}>
                                                {typeLabels[h.type]}
                                            </span>
                                        </td>
                                        <td className="holiday__td">
                                            <span className={`holiday__badge holiday__badge--${h.applies_to}`}>
                                                {appliesToLabels[h.applies_to]}
                                            </span>
                                        </td>
                                        <td className="holiday__td" style={{ textAlign: 'center' }}>
                                            <button
                                                className="holiday__action-btn"
                                                onClick={() => handleDelete(h.id)}
                                                title="Delete"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                /* ─── Calendar View ─── */
                <div className="holiday__calendar">
                    <div className="holiday__cal-nav">
                        <button
                            className="holiday__cal-nav-btn"
                            onClick={() => {
                                if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
                                else setCalMonth(calMonth - 1);
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        <span className="holiday__cal-month">
                            {new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                            className="holiday__cal-nav-btn"
                            onClick={() => {
                                if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
                                else setCalMonth(calMonth + 1);
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    </div>

                    <div className="holiday__cal-grid">
                        {WEEKDAYS.map((wd) => (
                            <div className="holiday__cal-weekday" key={wd}>{wd}</div>
                        ))}
                        {/* Empty cells before first day */}
                        {Array.from({ length: getFirstDayOfMonth(calYear, calMonth) }).map((_, i) => (
                            <div className="holiday__cal-day holiday__cal-day--empty" key={`e-${i}`} />
                        ))}
                        {/* Day cells */}
                        {Array.from({ length: getDaysInMonth(calYear, calMonth) }).map((_, i) => {
                            const day = i + 1;
                            const dateStr = toYYYYMMDD(calYear, calMonth, day);
                            const isToday = dateStr === todayStr;
                            const dayHolidays = calHolidaysByDay[day] || [];

                            return (
                                <div
                                    className={`holiday__cal-day ${isToday ? 'holiday__cal-day--today' : ''}`}
                                    key={day}
                                >
                                    <div className="holiday__cal-day-num">{day}</div>
                                    {dayHolidays.slice(0, 2).map((h) => (
                                        <span
                                            className={`holiday__cal-pill holiday__cal-pill--${h.type}`}
                                            key={h.id}
                                            title={`${h.name} (${typeLabels[h.type]})`}
                                        >
                                            {h.name}
                                        </span>
                                    ))}
                                    {dayHolidays.length > 2 && (
                                        <span className="holiday__cal-pill holiday__cal-pill--public" style={{ fontSize: 9, opacity: 0.7 }}>
                                            +{dayHolidays.length - 2} more
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ═══ Creation Drawer ═══ */}
            {drawerOpen && (
                <>
                    <div className="holiday__drawer-overlay" onClick={() => setDrawerOpen(false)} />
                    <div className="holiday__drawer">
                        <div className="holiday__drawer-header">
                            <h2 className="holiday__drawer-title">Add Holiday</h2>
                            <button className="holiday__drawer-close" onClick={() => setDrawerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="holiday__drawer-body">
                            {errorMsg && (
                                <div className="holiday__toast holiday__toast--error" style={{ position: 'static', animation: 'none' }}>
                                    {errorMsg}
                                </div>
                            )}

                            <div className="holiday__drawer-card">
                                <span className="holiday__drawer-card-title">Holiday Details</span>

                                <div className="holiday__field">
                                    <label className="holiday__label" htmlFor="holName">Name *</label>
                                    <input
                                        className="holiday__input"
                                        id="holName"
                                        type="text"
                                        placeholder="e.g. Republic Day"
                                        maxLength={100}
                                        value={formName}
                                        onChange={(e) => setFormName(e.target.value)}
                                    />
                                </div>

                                <div className="holiday__field-row">
                                    <div className="holiday__field">
                                        <label className="holiday__label" htmlFor="holType">Type</label>
                                        <select
                                            className="holiday__select"
                                            id="holType"
                                            value={formType}
                                            onChange={(e) => setFormType(e.target.value as HolidayType)}
                                        >
                                            {typeOptions.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="holiday__field">
                                        <label className="holiday__label">Applies To</label>
                                        <div className="holiday__segmented">
                                            {appliesToOptions.map((o) => (
                                                <button
                                                    key={o.value}
                                                    className={`holiday__seg-btn ${formAppliesTo === o.value ? 'holiday__seg-btn--active' : ''}`}
                                                    type="button"
                                                    onClick={() => setFormAppliesTo(o.value)}
                                                >
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="holiday__field-row">
                                    <div className="holiday__field">
                                        <label className="holiday__label" htmlFor="holStart">Start Date *</label>
                                        <input
                                            className="holiday__input"
                                            id="holStart"
                                            type="date"
                                            value={formStartDate}
                                            onChange={(e) => handleStartDateChange(e.target.value)}
                                        />
                                    </div>
                                    <div className="holiday__field">
                                        <label className="holiday__label" htmlFor="holEnd">End Date *</label>
                                        <input
                                            className="holiday__input"
                                            id="holEnd"
                                            type="date"
                                            value={formEndDate}
                                            min={formStartDate}
                                            onChange={(e) => setFormEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                                {formStartDate && formEndDate && formEndDate >= formStartDate && (
                                    <span className="holiday__date-hint">
                                        Duration: {computeDuration(formStartDate, formEndDate)}{' '}
                                        {computeDuration(formStartDate, formEndDate) === 1 ? 'day' : 'days'}
                                    </span>
                                )}

                                <div className="holiday__field">
                                    <label className="holiday__label" htmlFor="holDesc">Description</label>
                                    <textarea
                                        className="holiday__textarea"
                                        id="holDesc"
                                        placeholder="Optional description…"
                                        value={formDesc}
                                        onChange={(e) => setFormDesc(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="holiday__drawer-footer">
                            <button className="holiday__drawer-cancel" onClick={() => setDrawerOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="holiday__drawer-save"
                                onClick={handleSubmit}
                                disabled={!formValid || saving}
                            >
                                {saving ? 'Saving…' : 'Add Holiday'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
